/**
 * @fileoverview Personal insolvency identity fields (MOR-1858 QA-07)
 *
 * Purpose: the bankrupt's / debtor's name and date of birth, edited from the
 * Job identity section of job settings. A pre-appointment personal job has
 * no Appointment tab (that only exists once the firm is appointed), so
 * without this the identity captured at intake could never be corrected.
 * Both values feed the personal conflict-check brief (MOR-1847).
 *
 * Used by: `_layout.jobs.$jobId.settings.tsx` (Job identity section).
 */

import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { DatePicker } from "@/ui/composed/inputs/date-picker";

export interface PersonalIdentityValues {
  bankruptName: string;
  /** ISO date (YYYY-MM-DD) or "" when not recorded. */
  bankruptDateOfBirth: string;
}

interface PersonalIdentityFieldsProps {
  values: PersonalIdentityValues;
  onChange: (next: PersonalIdentityValues) => void;
  disabled: boolean;
}

export function PersonalIdentityFields({
  values,
  onChange,
  disabled,
}: PersonalIdentityFieldsProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="flex flex-col gap-2">
        <Label htmlFor="settingsBankruptName">Bankrupt Name</Label>
        <Input
          id="settingsBankruptName"
          placeholder="e.g. Alex Example"
          value={values.bankruptName}
          onChange={(e) =>
            onChange({ ...values, bankruptName: e.target.value })
          }
          disabled={disabled}
        />
        <p className="text-xs text-muted-foreground">
          The individual named on the conflict check and generated documents.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="settingsBankruptDateOfBirth">Date of Birth</Label>
        <DatePicker
          value={values.bankruptDateOfBirth}
          onChange={(next) =>
            onChange({ ...values, bankruptDateOfBirth: next ?? "" })
          }
          placeholder="DD/MM/YYYY"
          disabled={disabled}
        />
        <p className="text-xs text-muted-foreground">
          Shown as &ldquo;Not recorded&rdquo; on the conflict check when blank.
        </p>
      </div>
    </div>
  );
}
